/**
 * Shared sample-tray client (David 2026-07-13). Single source of truth for
 * adding/removing/reading fabric samples, consumed by both the material
 * popup (snippets/product-material-info.liquid) and the global floating
 * tray (snippets/sample-tray.liquid) so they never drift out of sync.
 *
 * SLO PORT 2026-07-28: catalog-driven instead of placeholder-driven.
 *
 * DE adds ONE placeholder variant (Muster-Anfrage) for every sample and tells
 * them apart only by cart line-item properties. Those two ids were hardcoded
 * here and are DE's, so on feydom-1 every add would have posted a variant that
 * does not exist.
 *
 * SLO already sells the samples as 52 real 0 EUR products, one per fabric, so
 * each add posts the ACTUAL sample product. The caller supplies props.variantId;
 * the set of sample product ids is injected once from Liquid via configure(),
 * because "is this cart line a sample?" can no longer be a single id compare.
 *
 * Still cart-backed, so the tray survives closing a popup, navigating, or
 * closing the browser.
 */
window.FeydomSamples = (function () {
  /* Filled by configure() from snippets/sample-tray.liquid, which is rendered
     once in layout/theme.liquid and therefore has Liquid access to the vzorci
     collection on every page. Empty until then: an unconfigured module reports
     zero samples rather than guessing. */
  var SAMPLE_PRODUCT_IDS = (window.FeydomSampleCatalog && window.FeydomSampleCatalog.productIds) || [];
  var MAX = 5;

  function configure(cfg) {
    if (cfg && cfg.productIds && cfg.productIds.length) {
      SAMPLE_PRODUCT_IDS = cfg.productIds;
    }
  }

  /* Primary signal is a hidden line-item property we set on every sample add,
     NOT the product-id list. Two reasons:
       - Liquid caps `collection.products` at 50 outside a paginate block, and
         `vzorci` already holds 52 products (49 live). The id list is one
         publish away from being silently incomplete.
       - The list is emitted by the tray snippet; anything that adds a sample
         before that runs would not be counted.
     The id list stays as a fallback so samples added by an older page still
     register after a deploy. */
  function isSampleLine(item) {
    var props = item.properties || {};
    if (props['_vzorec']) return true;
    return SAMPLE_PRODUCT_IDS.indexOf(item.product_id) !== -1;
  }
  var state = { count: 0, items: [], ready: false };
  var listeners = [];

  function normalize(item) {
    var props = item.properties || {};
    return {
      key: item.key,
      quantity: item.quantity,
      title: props['Izdelek'] || props['Produkt'] || '',
      material: props['Material'] || props['Barva'] || props['Farbe'] || '',
      image: props['_Slika izdelka'] || props['_Produktbild'] || '',
      url: props['_Stran izdelka'] || props['_Produktseite'] || ''
    };
  }

  function notify() {
    listeners.forEach(function (fn) {
      try { fn(state); } catch (e) {}
    });
    window.dispatchEvent(new CustomEvent('feydom:samples-updated', { detail: state }));
  }

  function refresh() {
    return fetch('/cart.js')
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        var items = cart.items.filter(isSampleLine);
        state.count = items.reduce(function (sum, i) { return sum + i.quantity; }, 0);
        state.items = items.map(normalize);
        state.ready = true;
        notify();
        return state;
      })
      .catch(function () {
        state.ready = true;
        return state;
      });
  }

  function isStaged(title, material) {
    return state.items.some(function (i) { return i.title === title && i.material === material; });
  }

  function findKey(title, material) {
    var match = state.items.filter(function (i) { return i.title === title && i.material === material; })[0];
    return match ? match.key : null;
  }

  function add(props) {
    if (state.count >= MAX) return Promise.reject(new Error('cap'));
    /* No variant means no sample product exists for this fabric (MX-01 / MX-02
       today). Fail loudly rather than posting a missing id. */
    if (!props || !props.variantId) return Promise.reject(new Error('no-sample'));
    var imageUrl = props.image || '';
    if (imageUrl.indexOf('//') === 0) imageUrl = 'https:' + imageUrl;
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [{
          id: props.variantId,
          quantity: 1,
          properties: {
            'Izdelek': props.title || '',
            'Material': props.material || '',
            '_Stran izdelka': props.url || '',
            '_Slika izdelka': imageUrl,
            '_vzorec': '1'
          }
        }]
      })
    }).then(function (res) {
      if (!res.ok) throw new Error('sample add failed');
      return refresh().then(function (s) {
        /* Clear confirmation of the action (David 2026-07-15: "no toast on
           button press"). The in-place button label swap was too subtle to
           register as "it worked". */
        window.dispatchEvent(new CustomEvent('feydom:sample-added', {
          detail: { title: props.title || '', material: props.material || '', count: s.count }
        }));
        return s;
      });
    });
  }

  function remove(lineKey) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: lineKey, quantity: 0 })
    }).then(function (res) {
      if (!res.ok) throw new Error('sample remove failed');
      return refresh().then(function (s) {
        window.dispatchEvent(new CustomEvent('feydom:sample-removed', { detail: { count: s.count } }));
        return s;
      });
    });
  }

  function removeByMatch(title, material) {
    var key = findKey(title, material);
    if (!key) return Promise.resolve(state);
    return remove(key);
  }

  function onUpdate(fn) {
    listeners.push(fn);
    if (state.ready) fn(state);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', refresh);
  } else {
    refresh();
  }

  return {
    MAX: MAX,
    configure: configure,
    add: add,
    remove: remove,
    removeByMatch: removeByMatch,
    refresh: refresh,
    isStaged: isStaged,
    onUpdate: onUpdate,
    getState: function () { return state; }
  };
})();
